import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../../context/StoreContext';
import { getStoreLabel } from '../../constants/stores';
import styles from './StoreSelector.module.css';

export default function StoreSelector(): React.ReactElement | null {
    const {
        activeStoreId,
        canSwitchStores,
        switchStore,
        storeOptions,
        isLoading
    } = useStore();

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (isLoading) {
        return <div className="nav-store-name">...</div>;
    }

    if (!activeStoreId) {
        return null;
    }

    if (!canSwitchStores) {
        return (
            <div className="nav-store-name">
                {getStoreLabel(activeStoreId)}
            </div>
        );
    }

    return (
        <div className={styles['store-selector']} ref={dropdownRef}>
            <button
                type="button"
                className={styles['store-selector-trigger']}
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                <span>{getStoreLabel(activeStoreId)}</span>
                <svg
                    className={`${styles['store-selector-chevron']} ${isOpen ? styles['open'] : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                >
                    <path
                        d="M2.5 4.5L6 8L9.5 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {isOpen && (
                <ul className={styles['store-selector-dropdown']} role="listbox">
                    {storeOptions.map((store) => (
                        <li
                            key={store.value}
                            role="option"
                            aria-selected={store.value === activeStoreId}
                            className={`${styles['store-selector-option']} ${store.value === activeStoreId ? styles['selected'] : ''}`}
                            onClick={() => {
                                switchStore(store.value);
                                setIsOpen(false);
                            }}
                        >
                            <span>{store.label}</span>
                            {store.value === activeStoreId && (
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path
                                        d="M2.5 7L5.5 10L11.5 4"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
